import { FadeUp, Overline, MaskedLines } from "@/components/Reveal";
import CompareSlider from "@/components/CompareSlider";
import { IMG } from "@/data/site";

export default function BeforeAfter() {
  return (
    <section id="before-after" data-testid="before-after-section" className="py-28 md:py-40 bg-charcoal/40 border-y hairline">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <FadeUp>
          <Overline>Before / After</Overline>
        </FadeUp>
        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-7xl leading-[0.98] tracking-tight">
            <MaskedLines lines={["SEE THE", "DIFFERENCE."]} />
          </h2>
          <FadeUp delay={0.2}>
            <p className="max-w-sm text-sm text-muted font-light leading-relaxed">
              Drag the divider. The details define the difference.
            </p>
          </FadeUp>
        </div>

        <FadeUp delay={0.1} className="mt-16">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-display text-2xl md:text-3xl font-light">Day to Dusk</h3>
            <span className="text-[10px] tracking-[0.3em] uppercase text-bronze">Client Project</span>
          </div>
          <CompareSlider
            testId="compare-day-to-dusk"
            before={IMG.dayToDuskBefore}
            after={IMG.dayToDuskAfter}
            ratio="aspect-[3/2]"
          />
        </FadeUp>

        <div className="mt-14 grid lg:grid-cols-3 gap-6">
          <FadeUp delay={0.1}>
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-display text-2xl md:text-3xl font-light">Flambient Interior Enhancement</h3>
              <span className="text-[10px] tracking-[0.3em] uppercase text-bronze">Client Project</span>
            </div>
            <CompareSlider
              testId="compare-flambient-interior"
              before={IMG.interiorBefore}
              after={IMG.interiorAfter}
              ratio="aspect-[3/2]"
            />
            <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-muted">
              Warm ambient capture — colour balanced, exposure blended, screen &amp; fireplace refined
            </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-display text-2xl md:text-3xl font-light">Bedroom HDR &amp; Window Pull</h3>
              <span className="text-[10px] tracking-[0.3em] uppercase text-bronze">Client Project</span>
            </div>
            <CompareSlider
              testId="compare-bedroom-hdr"
              before={IMG.bedroomBefore}
              after={IMG.bedroomAfter}
              ratio="aspect-[3/2]"
            />
            <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-muted">
              Dim mixed-light capture — white balanced, windows pulled, shadows lifted
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-display text-2xl md:text-3xl font-light">Sky Replacement &amp; Colour</h3>
              <span className="text-[10px] tracking-[0.3em] uppercase text-bronze">Client Project</span>
            </div>
            <CompareSlider
              testId="compare-sky-replacement"
              before={IMG.poolBefore}
              after={IMG.poolAfter}
              ratio="aspect-[3/2]"
            />
            <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-muted">
              Overcast capture — sky replaced, greens lifted, pool &amp; stone colour enriched
            </p>
          </FadeUp>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FadeUp delay={0.1}>
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-display text-xl md:text-2xl font-light">Architectural Exterior Enhancement</h3>
              <span className="text-[10px] tracking-[0.3em] uppercase text-bronze">Client Project</span>
            </div>
            <CompareSlider
              testId="compare-architectural-exterior"
              before={IMG.towerBefore}
              after={IMG.towerAfter}
              ratio="aspect-[16/11]"
            />
            <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-muted">
              Flat daylight capture — sky enriched, fa&ccedil;ade contrast &amp; colour refined
            </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-display text-xl md:text-2xl font-light">Window Pull &amp; Colour</h3>
              <span className="text-[10px] tracking-[0.3em] uppercase text-bronze">Client Project</span>
            </div>
            <CompareSlider
              testId="compare-window-pull-colour"
              before={IMG.windowPullBefore}
              after={IMG.windowPullAfter}
              ratio="aspect-[16/11]"
            />
            <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-muted">
              Dim ambient capture — window view pulled, white balance &amp; colour refined
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-display text-xl md:text-2xl font-light">Natural Twilight</h3>
              <span className="text-[10px] tracking-[0.3em] uppercase text-bronze">Client Project</span>
            </div>
            <CompareSlider
              testId="compare-natural-twilight"
              before={IMG.twilightRanchBefore}
              after={IMG.twilightRanchAfter}
              ratio="aspect-[16/11]"
            />
            <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-muted">
              Flat dusk capture — twilight sky, warm windows, lawn &amp; colour revived
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
